import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Offcanvas, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { Shield, Key, Check, Loader2, ChevronDown } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  useFetchAllPermissions,
  useUpdateRole,
  useUpdateRolePermissions,
} from '../../services/roles';

const CATEGORY_CONFIG = {
  claims: { label: 'Claims', color: 'blue' },
  users: { label: 'Users', color: 'cyan' },
  invitations: { label: 'Invitations', color: 'emerald' },
  reports: { label: 'Reports', color: 'amber' },
  admin: { label: 'Administrative', color: 'red' },
  files: { label: 'Files', color: 'slate' },
  calendar: { label: 'Calendar', color: 'cyan' },
  partners: { label: 'Partners', color: 'slate' },
  provider: { label: 'Provider', color: 'blue' },
  support: { label: 'Support', color: 'cyan' },
  roles: { label: 'Roles', color: 'red' },
  permissions: { label: 'Permissions', color: 'red' },
};

const RoleEditorPanel = ({ show, onHide, role, onSuccess }) => {
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState('details');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [initialDisplayName, setInitialDisplayName] = useState('');
  const [initialDescription, setInitialDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [initialPermissions, setInitialPermissions] = useState(new Set());

  // Data fetching
  const { data: allPermissions, isLoading: permissionsLoading } = useFetchAllPermissions();

  // Mutations
  const updateRole = useUpdateRole(
    () => {
      toast.success('Role details updated');
      queryClient.invalidateQueries(['roles']);
      setInitialDisplayName(displayName);
      setInitialDescription(description);
      if (onSuccess) onSuccess();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || 'Failed to update role');
      },
    }
  );

  const updatePermissions = useUpdateRolePermissions(
    () => {
      toast.success('Permissions updated');
      queryClient.invalidateQueries(['roles']);
      setInitialPermissions(new Set(selectedPermissions));
      if (onSuccess) onSuccess();
    },
    {
      onError: (error) => {
        toast.error(error.response?.data?.msg || 'Failed to update permissions');
      },
    }
  );

  // Reset state when role changes
  useEffect(() => {
    if (role) {
      setActiveTab('details');
      setDisplayName(role.roleDisplayName || '');
      setDescription(role.roleDescription || '');
      setInitialDisplayName(role.roleDisplayName || '');
      setInitialDescription(role.roleDescription || '');

      const perms = new Set(role.permissions || []);
      setSelectedPermissions(perms);
      setInitialPermissions(perms);
    }
  }, [role]);

  // Changes detection
  const hasDetailsChanges = useMemo(() => {
    return displayName !== initialDisplayName || description !== initialDescription;
  }, [displayName, description, initialDisplayName, initialDescription]);

  const hasPermissionsChanges = useMemo(() => {
    if (selectedPermissions.size !== initialPermissions.size) return true;
    for (const perm of selectedPermissions) {
      if (!initialPermissions.has(perm)) return true;
    }
    return false;
  }, [selectedPermissions, initialPermissions]);

  const hasAnyChanges = activeTab === 'details' ? hasDetailsChanges : hasPermissionsChanges;

  // Group permissions by category
  const permissionsByCategory = useMemo(() => {
    if (!allPermissions) return {};

    const grouped = {};
    allPermissions
      .filter((perm) => !role?.roleGroup || perm.permissionGroups?.includes(role.roleGroup))
      .forEach((perm) => {
        const category = perm.permissionCategory || 'other';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(perm);
      });

    return grouped;
  }, [allPermissions, role?.roleGroup]);

  // Toggle permission
  const togglePermission = useCallback((permissionKey) => {
    setSelectedPermissions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(permissionKey)) {
        newSet.delete(permissionKey);
      } else {
        newSet.add(permissionKey);
      }
      return newSet;
    });
  }, []);

  // Save handlers
  const handleSave = useCallback(() => {
    if (activeTab === 'details') {
      updateRole.mutate({
        roleId: role.roleId,
        displayName: displayName.trim(),
        description: description.trim(),
      });
    } else {
      updatePermissions.mutate({
        roleId: role.roleId,
        permissionKeys: Array.from(selectedPermissions),
      });
    }
  }, [activeTab, role?.roleId, displayName, description, selectedPermissions, updateRole, updatePermissions]);

  const isSaving = updateRole.isLoading || updatePermissions.isLoading;

  if (!role) return null;

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: '500px' }}
      className="d-flex flex-column"
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <div className="flex-1 min-w-0">
          <h5 className="mb-1 font-semibold">{role.roleDisplayName}</h5>
          <p className="text-xs text-slate-400 font-mono mb-0">{role.roleName}</p>
        </div>
      </Offcanvas.Header>

      {/* Tab bar */}
      <div className="px-4 pt-3 pb-0">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-all rounded-md',
              activeTab === 'details'
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            )}
          >
            <Shield className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Details
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition-all rounded-md',
              activeTab === 'permissions'
                ? 'bg-white shadow-sm text-slate-900'
                : 'text-slate-500 hover:text-slate-700'
            )}
          >
            <Key className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Permissions
          </button>
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <Offcanvas.Body className="p-0 flex-1 overflow-y-auto">
          {activeTab === 'details' && (
            <div className="px-4 py-4">
              <Form.Group className="mb-3">
                <Form.Label className="text-sm font-medium text-slate-700">
                  Display Name
                </Form.Label>
                <Form.Control
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g., Claims Administrator"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="text-sm font-medium text-slate-700">
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this role is for..."
                />
              </Form.Group>
            </div>
          )}

          {activeTab === 'permissions' && (
            <>
              {permissionsLoading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              )}

              {!permissionsLoading && (
                <PermissionsAccordion
                  permissionsByCategory={permissionsByCategory}
                  selectedPermissions={selectedPermissions}
                  onTogglePermission={togglePermission}
                />
              )}
            </>
          )}
        </Offcanvas.Body>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
          {hasAnyChanges && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Unsaved changes
            </div>
          )}
          {!hasAnyChanges && <div />}
          <button
            onClick={handleSave}
            disabled={!hasAnyChanges || isSaving}
            className={cn(
              'inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              hasAnyChanges && !isSaving
                ? 'hover:bg-blue-700'
                : 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {activeTab === 'details' ? 'Save Details' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </Offcanvas>
  );
};

// PermissionsAccordion Component.
// Plain Tailwind collapsible (state + conditional render) — NOT react-bootstrap's
// Accordion, whose animated Collapse desyncs under React 18 StrictMode's dev
// double-render (header shows open while the body stays hidden).
const PermissionsAccordion = ({ permissionsByCategory, selectedPermissions, onTogglePermission }) => {
  const [openCategories, setOpenCategories] = useState(() => new Set());

  const toggleCategory = useCallback((category) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  return (
    <div className="px-4">
      {Object.entries(permissionsByCategory).map(([category, permissions]) => {
        const config = CATEGORY_CONFIG[category] || {
          label: category,
          color: 'slate',
        };

        const enabledCount = permissions.filter((p) =>
          selectedPermissions.has(p.permissionKey)
        ).length;
        const isOpen = openCategories.has(category);

        return (
          <div key={category} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between rounded-md px-1 py-2.5 text-left transition-colors hover:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600">
                  {config.label}
                </span>
                <span className="text-xs text-slate-500">
                  {enabledCount}/{permissions.length}
                </span>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </button>

            {isOpen && (
              <div className="pb-3 pt-1">
                <div className="divide-y divide-slate-100">
                  {permissions.map((permission) => (
                    <PermissionRow
                      key={permission.permissionId || permission.permissionKey}
                      permission={permission}
                      isEnabled={selectedPermissions.has(permission.permissionKey)}
                      onToggle={onTogglePermission}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// PermissionRow Component
const PermissionRow = ({ permission, isEnabled, onToggle }) => {
  return (
    <div
      onClick={() => onToggle(permission.permissionKey)}
      className="flex items-start justify-between py-2 px-1 cursor-pointer transition-colors hover:bg-slate-50"
    >
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        <Form.Check
          type="switch"
          checked={isEnabled}
          onChange={() => onToggle(permission.permissionKey)}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 mb-0.5">
            {permission.permissionDisplayName}
          </p>
          {permission.permissionDescription && (
            <p className="text-xs text-slate-500 mb-0">
              {permission.permissionDescription}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleEditorPanel;
